import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useInstallerJobs } from "@/lib/queries";
import { INSTALL_STATUS_LABELS, SERVICE_STATUS_LABELS, weekLabel, formatDate, formatDateLong, addressLine } from "@/lib/format";
import { LoadingBlock, ErrorBlock } from "@/components/common/Blocks";
import { StateChip, toneForInstallStatus } from "@/components/common/Badges";
import { InstallerShell } from "@/components/layout/InstallerShell";

const JobRow = ({ j }) => {
  const to = j.kind === "service" ? `/service/${j.service_id}` : `/montor/${j.delivery_id}`;
  const basis = j.kind === "montering" ? (j.released ? { t: "Grunnlag frigitt", tone: "released" } : { t: j.production_status === "ferdig" ? "Grunnlag ikke frigitt" : "Ikke ferdig produsert", tone: "waiting" }) : null;
  return (
    <li>
      <Link to={to} className="flex min-h-[72px] items-center gap-3 py-3 active:bg-secondary" data-testid="installer-job-card">
        <div className="w-[64px] shrink-0">
          <p className="text-[15px] font-semibold tabular">{j.date ? formatDate(j.date) : weekLabel(j.week)}</p>
          {j.time_window && <p className="t-meta">{j.time_window}</p>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-semibold leading-snug">{j.project_name}</p>
          <p className="t-meta truncate">{j.kind === "service" ? `Service · ${j.title}` : `Montering · ${j.stair_summary}${j.quantity > 1 ? ` · ${j.quantity} trapper` : ""}`}</p>
          <p className="t-meta truncate">{addressLine(j.address)}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {j.kind === "service" ? <StateChip tone="ready">{SERVICE_STATUS_LABELS[j.status]}</StateChip> : <StateChip tone={toneForInstallStatus(j.status)}>{INSTALL_STATUS_LABELS[j.status] || j.status}</StateChip>}
            {basis && <StateChip tone={basis.tone} strong={basis.tone === "waiting"}>{basis.t}</StateChip>}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
};

export default function MontorPage() {
  const { data, isLoading, isError, refetch } = useInstallerJobs();
  if (isLoading) return <InstallerShell title="Montør"><LoadingBlock /></InstallerShell>;
  if (isError) return <InstallerShell title="Montør"><ErrorBlock onRetry={refetch} /></InstallerShell>;
  const today = data.jobs.filter((j) => j.is_today);
  const later = data.jobs.filter((j) => !j.is_today);
  return (
    <InstallerShell title="Montør · dagens jobber">
      <div className="mb-4"><h1 className="text-[24px] font-semibold leading-tight">I dag</h1><p className="t-meta mt-0.5">{formatDateLong(data.today)} · {today.length ? `${today.length} jobb${today.length > 1 ? "er" : ""}` : "ingen jobber"}</p></div>
      <ul className="divide-y border-y" data-testid="installer-today">
        {today.length ? today.map((j) => <JobRow key={`${j.kind}-${j.number}`} j={j} />) : <li className="py-6 t-body text-muted-foreground">Ingen jobber i dag. Kommende jobber vises under.</li>}
      </ul>
      <h2 className="mb-2 mt-8 text-[15px] font-semibold">Kommende</h2>
      <ul className="divide-y border-y" data-testid="installer-upcoming">
        {later.length ? later.map((j) => <JobRow key={`${j.kind}-${j.number}`} j={j} />) : <li className="py-6 t-body text-muted-foreground">Ingen planlagte jobber de neste ukene.</li>}
      </ul>
    </InstallerShell>
  );
}
